/**
 * The internal "/t/{slug}/…" path prefix, as seen from a TENANT host.
 *
 * On a tenant host (subdomain or verified custom domain) the practice is already fixed by
 * the Host header, so "/t/{slug}" is redundant — and it is routed nowhere in the tenant
 * tree, so following one rendered a permanently blank page. AdminLayout builds the
 * sidebar's "View site" link as "/t/{slug}" whenever the tenant has no custom domain,
 * which puts the link on exactly the hosts where it does not resolve.
 *
 * Lives in its own module so the decision below can be tested without importing
 * TenantSite.jsx, which pulls the whole admin route tree behind it.
 */

/**
 * Does the practice served by this host answer to `pathSlug`?
 *
 * A practice is addressable by slug, subdomain or immutable tenant id — the same three
 * keys the backend's tenant lookup accepts — so any of them is a legitimate way to name
 * it. Anything else names a DIFFERENT practice, and must not be redirected to this one:
 * answering a request for someone else's practice with this practice's home page renders
 * the wrong identity under a URL that asked for another, which is the same class of
 * defect as hardcoded branding.
 *
 * @param {string} pathSlug   the ":slug" segment taken from the URL
 * @param {object|null} tenant  the tenant record this host resolved to
 * @param {string} hostSlug   the slug the TenantProvider was mounted with
 * @returns {boolean}
 */
export function hostOwnsSlug(pathSlug, tenant, hostSlug) {
  const wanted = String(pathSlug || "").trim().toLowerCase();
  if (!wanted) return false;
  return [hostSlug, tenant?.slug, tenant?.subdomain, tenant?.tenant_id]
    .filter(Boolean)
    .map((s) => String(s).trim().toLowerCase())
    .includes(wanted);
}
